import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../../firebase";

import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import "../../admin/AdminDashboard.css";

const GOOGLE_API_KEY = "AIzaSyA93FPo9uPGHk0g8FFvaZSKokn4n1tLmag";

export default function EditTrail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => {
    async function loadTrail() {
      try {
        const refDoc = doc(db, "trails", id);
        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          alert("Trail not found");
          navigate("/trailseditor");
          return;
        }

        setTrail(snap.data());
      } catch (err) {
        console.error("Error loading:", err);
        alert("Error loading trail");
      } finally {
        setLoading(false);
      }
    }
    loadTrail();
  }, [id, navigate]);

  if (loading) return <div className="admin-page">Loading…</div>;
  if (!trail) return <div className="admin-page">Trail not found.</div>;

  function safeFolderName(str) {
    return (str || id)
      .trim()
      .replace(/[/\\]/g, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_");
  }

  function updateField(key, val) {
    setTrail((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);

    try {
      await updateDoc(doc(db, "trails", id), {
        name: trail.name,
        city: trail.city,
        description: trail.description,
        isPaid: trail.isPaid,
        priceEUR: trail.priceEUR || 0,
        mapURL: trail.mapURL || "",
        bannerImageURL: trail.bannerImageURL || "",
      });

      alert("Trail updated successfully!");
      navigate("/trailseditor");
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving trail.");
    }

    setSaving(false);
  }

  async function deleteBanner() {
    if (!trail.bannerImageURL) return;
    if (!window.confirm("Delete banner image?")) return;

    try {
      const url = trail.bannerImageURL;
      const path = decodeURIComponent(url.split("/o/")[1].split("?")[0]);
      await deleteObject(ref(storage, path));

      setTrail((prev) => ({ ...prev, bannerImageURL: "" }));
      alert("Banner deleted.");
    } catch (err) {
      console.error("Delete banner error:", err);
      alert("Error deleting banner");
    }
  }

  async function handleBannerUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBanner(true);

    try {
      const safeName = safeFolderName(trail.name);
      const path = `trails/${safeName}/banner_${Date.now()}.jpg`;
      const refObj = ref(storage, path);

      await uploadBytes(refObj, file);
      const url = await getDownloadURL(refObj);

      setTrail((prev) => ({ ...prev, bannerImageURL: url }));
      alert("Banner uploaded successfully!");
    } catch (err) {
      console.error("Banner upload error:", err);
      alert("Error uploading banner");
    }

    setUploadingBanner(false);
  }

  async function deleteTrail() {
    if (!window.confirm("Are you sure you want to delete this entire trail? This cannot be undone.")) {
      return;
    }

    try {
      // Delete banner image from storage
      if (trail.bannerImageURL) {
        const path = decodeURIComponent(
          trail.bannerImageURL.split("/o/")[1].split("?")[0]
        );
        await deleteObject(ref(storage, path)).catch(() => {});
      }

      // Delete trail document from Firestore
      await deleteDoc(doc(db, "trails", id));

      alert("Trail deleted successfully.");
      navigate("/trailseditor");

    } catch (err) {
      console.error("Delete trail error:", err);
      alert("Error deleting trail.");
    }
  }

  // Validate coordinates format
  function isValidCoordinates(coordString) {
    if (!coordString) return false;
    const parts = coordString.split(",");
    if (parts.length !== 2) return false;
    
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    
    return !isNaN(lat) && !isNaN(lng) && 
           lat >= -90 && lat <= 90 && 
           lng >= -180 && lng <= 180;
  }

  return (
    <div className="admin-page">
      <h1>Edit Trail</h1>
      <p className="admin-subtitle">Update trail information and settings</p>

      <form className="admin-form-large" onSubmit={handleSave}>
        
        {/* BASIC INFORMATION */}
        <div className="section-box">
          <h2 className="section-title">📋 Basic Information</h2>

          <label className="admin-label">Trail Name</label>
          <input
            className="admin-input"
            value={trail.name}
            onChange={(e) => updateField("name", e.target.value)}
            required
          />

          <label className="admin-label">County/Location</label>
          <select
            className="admin-input"
            value={trail.city}
            onChange={(e) => updateField("city", e.target.value)}
            required
          >
            <option value="">Select Location</option>
            <option value="Ireland">Ireland</option>
            <option value="Antrim">Antrim</option>
            <option value="Armagh">Armagh</option>
            <option value="Carlow">Carlow</option>
            <option value="Cavan">Cavan</option>
            <option value="Clare">Clare</option>
            <option value="Cork">Cork</option>
            <option value="Derry">Derry</option>
            <option value="Donegal">Donegal</option>
            <option value="Down">Down</option>
            <option value="Dublin">Dublin</option>
            <option value="Fermanagh">Fermanagh</option>
            <option value="Galway">Galway</option>
            <option value="Kerry">Kerry</option>
            <option value="Kildare">Kildare</option>
            <option value="Kilkenny">Kilkenny</option>
            <option value="Laois">Laois</option>
            <option value="Leitrim">Leitrim</option>
            <option value="Limerick">Limerick</option>
            <option value="Longford">Longford</option>
            <option value="Louth">Louth</option>
            <option value="Mayo">Mayo</option>
            <option value="Meath">Meath</option>
            <option value="Monaghan">Monaghan</option>
            <option value="Offaly">Offaly</option>
            <option value="Roscommon">Roscommon</option>
            <option value="Sligo">Sligo</option>
            <option value="Tipperary">Tipperary</option>
            <option value="Tyrone">Tyrone</option>
            <option value="Waterford">Waterford</option>
            <option value="Westmeath">Westmeath</option>
            <option value="Wexford">Wexford</option>
            <option value="Wicklow">Wicklow</option>
          </select>

          <label className="admin-label">Description</label>
          <textarea
            className="admin-input admin-textarea"
            rows="6"
            value={trail.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Describe the trail, its theme, difficulty, and what makes it special..."
            required
          />
        </div>

        {/* PRICING */}
        <div className="section-box">
          <h2 className="section-title">💰 Pricing</h2>

          <label className="admin-label">Trail Type</label>
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input
                type="radio"
                name="trailType"
                checked={!trail.isPaid}
                onChange={() => updateField("isPaid", false)}
                style={{ marginRight: "8px" }}
              />
              <span>Free Trail</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input
                type="radio"
                name="trailType"
                checked={trail.isPaid}
                onChange={() => updateField("isPaid", true)}
                style={{ marginRight: "8px" }}
              />
              <span>Paid Trail</span>
            </label>
          </div>

          {trail.isPaid && (
            <>
              <label className="admin-label">Price (EUR €)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="admin-input"
                value={trail.priceEUR ?? 0}
                onChange={(e) => updateField("priceEUR", Number(e.target.value))}
                style={{ maxWidth: "200px" }}
              />
            </>
          )}
        </div>

        {/* MAP LOCATION */}
        <div className="section-box">
          <h2 className="section-title">📍 Starting Location</h2>

          <label className="admin-label">
            Coordinates (Latitude, Longitude)
            <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "8px" }}>
              Format: 53.2743872, -9.0518509
            </span>
          </label>
          <input
            className="admin-input"
            value={trail.mapURL || ""}
            placeholder="53.2743872, -9.0518509"
            onChange={(e) => updateField("mapURL", e.target.value)}
            style={{ fontFamily: "monospace" }}
          />

          {trail.mapURL && isValidCoordinates(trail.mapURL) && (() => {
            const parts = trail.mapURL.split(",");
            const lat = parts[0].trim();
            const lng = parts[1].trim();
            const embedUrl = `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_API_KEY}&center=${lat},${lng}&zoom=16`;

            return (
              <>
                <p style={{ color: "#4CAF50", fontSize: "0.9em", marginTop: "8px" }}>
                  ✓ Valid coordinates
                </p>
                <iframe
                  title="Map Preview"
                  style={{
                    marginTop: "12px",
                    width: "100%",
                    height: "320px",
                    border: "0",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                  }}
                  src={embedUrl}
                  loading="lazy"
                />
              </>
            );
          })()}

          {trail.mapURL && !isValidCoordinates(trail.mapURL) && (
            <p style={{ color: "#f44336", fontSize: "0.9em", marginTop: "8px" }}>
              ⚠️ Invalid format. Please use: latitude, longitude (e.g., 53.2743872, -9.0518509)
            </p>
          )}
        </div>

        {/* BANNER IMAGE */}
        <div className="section-box">
          <h2 className="section-title">🖼️ Banner Image</h2>

          {trail.bannerImageURL && (
            <div style={{ marginBottom: "20px" }}>
              <img
                src={trail.bannerImageURL}
                alt="Trail Banner"
                style={{
                  width: "100%",
                  maxHeight: "300px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  marginBottom: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}
              />
              <button
                type="button"
                className="admin-button danger"
                onClick={deleteBanner}
              >
                🗑 Delete Banner
              </button>
            </div>
          )}

          <label className="admin-label">
            {trail.bannerImageURL ? "Replace Banner Image" : "Upload Banner Image"}
          </label>
          <input
            type="file"
            accept="image/*"
            className="admin-input"
            onChange={handleBannerUpload}
          />

          {uploadingBanner && (
            <p style={{ color: "#2196F3", marginTop: "8px" }}>
              Uploading banner...
            </p>
          )}
        </div>

        {/* ACTION BUTTONS */}
        <div style={{ 
          display: "flex", 
          gap: "12px", 
          marginTop: "30px",
          paddingTop: "20px",
          borderTop: "2px solid #eee"
        }}>
          <button 
            type="submit" 
            className="admin-button" 
            disabled={saving}
            style={{ flex: 1 }}
          >
            {saving ? "Saving..." : "💾 Save Changes"}
          </button>

          <button
            type="button"
            className="admin-button secondary"
            onClick={() => navigate("/trailseditor")}
            style={{ flex: 1 }}
          >
            Cancel
          </button>
        </div>

        {/* MANAGE CLUES LINK */}
        <div style={{ 
          marginTop: "30px", 
          paddingTop: "20px", 
          borderTop: "2px solid #e3f2fd" 
        }}>
          <button
            type="button"
            className="admin-button"
            onClick={() => navigate(`/trailseditor/${id}/clues`)}
            style={{ width: "100%" }}
          >
            🔍 Manage Clues
          </button>
        </div>

        {/* DELETE TRAIL */}
        <div style={{ 
          marginTop: "40px", 
          paddingTop: "30px", 
          borderTop: "2px solid #ffebee" 
        }}>
          <h3 style={{ color: "#f44336", marginBottom: "12px" }}>
            ⚠️ Danger Zone
          </h3>
          <p style={{ 
            fontSize: "0.9em", 
            color: "#666", 
            marginBottom: "12px" 
          }}>
            Deleting a trail is permanent and cannot be undone. All associated data will be lost.
          </p>
          <button
            type="button"
            className="admin-button danger"
            onClick={deleteTrail}
          >
            🗑 Delete Entire Trail
          </button>
        </div>

      </form>
    </div>
  );
}